import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Phone, PhoneOff, Mic, Delete, X, Users, PhoneForwarded, ChevronLeft, Clock, Play, Pause, Download } from "lucide-react";
import { useSoftphone } from "./SoftphoneProvider";
import { AudioPlayer } from "@/components/common/AudioPlayer";
import { AutoDialerPainel } from "./AutoDialerPainel";
import { useAutoDialerSessao } from "@/hooks/useAutoDialer";

const TIPO_LABEL: Record<string, string> = { ramal: "interna", manual: "manual", recebida: "recebida", discadora: "discadora", ura: "URA" };

function fmtDur(s: number | null): string {
  if (!s || s < 0) return "0:00";
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}
function fmtData(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => (n < 10 ? "0" : "") + n;
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fmtTel(t: string | null): string {
  if (!t) return "—";
  let d = t.replace(/\D/g, "");
  if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return t;
}
function nomeArquivoGravacao(h: { destino: string | null; ramal_origem: string | null; inicio: string | null; created_at: string; usuario_nome: string | null }): string {
  const linha = (h.destino || "").replace(/\D/g, "") || "sem-numero";
  const ramal = h.ramal_origem || "sem-ramal";
  const d = h.inicio || h.created_at;
  let dt = "";
  if (d) { const x = new Date(d); const p = (n: number) => (n < 10 ? "0" : "") + n; dt = `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}_${p(x.getHours())}h${p(x.getMinutes())}`; }
  const nome = (h.usuario_nome || "sem-nome").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().replace(/\s+/g, "-").replace(/[^A-Za-z0-9-]/g, "") || "sem-nome";
  return `${linha}_${ramal}_${dt}_${nome}.mp3`;
}

// Acima de qualquer modal/dialog/toast — o softphone fica sempre na frente.
const SOFTPHONE_Z = 2147483000;

// ⛔ `data-softphone-ui` nos DOIS elementos fixed acima (botao + painel) — e'
// o que `components/ui/dialog.tsx` procura pra NAO fechar o modal quando o
// clique (discar, atender, desligar, teclado) e' aqui dentro. O painel e'
// portalado direto pra `document.body`, fora da arvore do DialogContent — sem
// esse marcador o Radix Dialog trata qualquer clique aqui como "clique de
// fora" e fecha o modal por baixo (Douglas 03/09/2026: "quando o consultor
// realiza a ligacao pelo softphone, o lead acaba sendo fechado").


const KEYS: { k: string; s?: string }[] = [
  { k: "1" }, { k: "2", s: "ABC" }, { k: "3", s: "DEF" },
  { k: "4", s: "GHI" }, { k: "5", s: "JKL" }, { k: "6", s: "MNO" },
  { k: "7", s: "PQRS" }, { k: "8", s: "TUV" }, { k: "9", s: "WXYZ" },
  { k: "*" }, { k: "0", s: "+" }, { k: "#" },
];

function statusLabel(s: string): { txt: string; color: string } {
  if (s === "registrado") return { txt: "registrado", color: "#34d399" };
  if (s === "conectando" || s === "carregando") return { txt: "conectando…", color: "#f0b429" };
  if (s === "sem-ramal") return { txt: "sem ramal", color: "#9ca3af" };
  return { txt: "desconectado", color: "#f87171" };
}

function initials(name: string | null): string {
  if (!name) return "?";
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
}

function Avatar({ foto, nome, size }: { foto?: string | null; nome: string | null; size: number }) {
  const [erro, setErro] = useState(false);
  if (foto && !erro) {
    return (
      <img
        src={foto}
        alt=""
        onError={() => setErro(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#f0e2e6" }}
      />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "#f0e2e6", color: "#6d1a2e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.4), fontWeight: 600, flexShrink: 0 }}>
      {initials(nome)}
    </div>
  );
}

export function FloatingSoftphone() {
  const sp = useSoftphone();
  const emSalaDiscadora = sp.motorSalaAtiva
    || sp.number.replace(/\D/g, "") === "7000"
    || sp.contactName?.toLocaleLowerCase("pt-BR").includes("sala da discadora");
  const st = statusLabel(sp.status);
  // AUTO DIALER (Douglas 06/10/2026): com a discagem aberta, o softphone vira o Auto Dialer;
  // encerrou, volta a ser o de hoje. A ligação manual (teclado, Ligar do lead) não muda.
  const autoDialer = useAutoDialerSessao(sp.status !== "sem-ramal");
  // "Fechar" do resumo final vale para a sessão, não só para esta tela: recarregar não traz o card de volta.
  const [autoDialerFechado, setAutoDialerFechadoEstado] = useState<string | null>(() => {
    try { return localStorage.getItem('autoDialer.fechado'); } catch { return null; }
  });
  const setAutoDialerFechado = (id: string) => {
    setAutoDialerFechadoEstado(id);
    try { localStorage.setItem('autoDialer.fechado', id); } catch { /* sem storage: some só nesta tela */ }
  };
  const adEstado = autoDialer.data && autoDialer.data.sessao.id !== autoDialerFechado ? autoDialer.data : null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [view, setView] = useState<"dial" | "ramais" | "historico">("dial");
  const [filtro, setFiltro] = useState("");
  const [buscaHist, setBuscaHist] = useState("");
  const [transferindo, setTransferindo] = useState(false);
  const [playId, setPlayId] = useState<string | null>(null);
  const [playUrl, setPlayUrl] = useState<string | null>(null);
  const [loadingPlay, setLoadingPlay] = useState<string | null>(null);

  // Botão arrastável (posição salva em localStorage) — right/bottom em px.
  const [pos, setPos] = useState<{ right: number; bottom: number }>(() => {
    try { const s = localStorage.getItem("softphone_pos"); if (s) { const p = JSON.parse(s); if (typeof p?.right === "number" && typeof p?.bottom === "number") return p; } } catch { /* noop */ }
    // bottom 84: acima do rodapé das tabelas — o FAB não pode cobrir dado
    // (punch-list Planilha §9). Continua arrastável; posição salva vence.
    return { right: 24, bottom: 84 };
  });
  const posRef = useRef(pos);
  const dragRef = useRef<{ sx: number; sy: number; sr: number; sb: number; moved: boolean } | null>(null);

  const onDragStart = (e: React.PointerEvent) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, sr: pos.right, sb: pos.bottom, moved: false };
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* noop */ }
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) d.moved = true;
    if (!d.moved) return;
    const right = Math.min(Math.max(d.sr - dx, 8), window.innerWidth - 64);
    const bottom = Math.min(Math.max(d.sb - dy, 8), window.innerHeight - 64);
    const np = { right, bottom };
    posRef.current = np;
    setPos(np);
  };
  const onDragEnd = () => {
    const d = dragRef.current; dragRef.current = null;
    if (!d) return;
    if (d.moved) { try { localStorage.setItem("softphone_pos", JSON.stringify(posRef.current)); } catch { /* noop */ } }
    else { sp.setOpen(!sp.open); }
  };

  useEffect(() => {
    if (!sp.inCall) setTransferindo(false);
  }, [sp.inCall]);

  useEffect(() => {
    if (sp.open && !sp.inCall && view === "dial") {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [sp.open, sp.inCall, view]);

  const abrirRamais = () => {
    setView((v) => {
      const nova = v === "ramais" ? "dial" : "ramais";
      if (nova === "ramais") sp.carregarRamais();
      return nova;
    });
  };

  const abrirHistorico = () => {
    setView((v) => {
      const nova = v === "historico" ? "dial" : "historico";
      if (nova === "historico") sp.carregarHistorico();
      return nova;
    });
  };

  const tocarGravacao = async (id: string) => {
    if (playId === id && playUrl) { setPlayId(null); setPlayUrl(null); return; }
    setLoadingPlay(id);
    const url = await sp.urlGravacao(id);
    setLoadingPlay(null);
    if (url) { setPlayId(id); setPlayUrl(url); }
  };

  const baixarGravacao = async (h: { id: string; destino: string | null; ramal_origem: string | null; inicio: string | null; created_at: string; usuario_nome: string | null }) => {
    const url = playId === h.id && playUrl ? playUrl : await sp.urlGravacao(h.id);
    if (!url) return;
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = nomeArquivoGravacao(h);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
    } catch { window.open(url, "_blank"); }
  };

  const grupos = useMemo(() => {
    const lista = sp.ramais || [];
    const q = filtro.trim().toLowerCase();
    const filt = q
      ? lista.filter((r) => `${r.nome} ${r.ramal} ${r.equipe}`.toLowerCase().includes(q))
      : lista;
    const map = new Map<string, typeof lista>();
    for (const r of filt) {
      const k = r.equipe || "Sem equipe";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return Array.from(map, ([equipe, itens]) => ({ equipe, itens }));
  }, [sp.ramais, filtro]);

  const histFiltrado = useMemo(() => {
    const lista = sp.historico || [];
    const raw = buscaHist.trim();
    if (!raw) return lista;
    const qn = raw.replace(/\D/g, "");
    const qt = raw.toLowerCase();
    return lista.filter((h) => {
      const dnum = (h.destino || "").replace(/\D/g, "");
      return (!!qn && dnum.includes(qn)) || (h.usuario_nome || "").toLowerCase().includes(qt) || (h.ramal_origem || "").includes(raw);
    });
  }, [sp.historico, buscaHist]);

  // Ao receber uma chamada, carrega os ramais pra resolver o nome de quem liga.
  useEffect(() => {
    if (sp.incoming && !sp.ramais) sp.carregarRamais();
  }, [sp.incoming, sp.ramais]);

  // Presença dos ramais (online/offline) enquanto a agenda está aberta — atualiza a cada 20s.
  useEffect(() => {
    const mostrandoAgenda = sp.open && (view === "ramais" || (sp.inCall && transferindo));
    if (!mostrandoAgenda) return;
    sp.carregarPresenca();
    const iv = setInterval(() => sp.carregarPresenca(), 20000);
    return () => clearInterval(iv);
  }, [sp.open, view, sp.inCall, transferindo]);

  const presencaSet = useMemo(() => new Set(sp.presenca), [sp.presenca]);

  const inbound = useMemo(() => {
    const m = sp.incomingFrom && sp.ramais ? sp.ramais.find((r) => r.ramal === sp.incomingFrom) : null;
    return { nome: m?.nome ?? null, foto: m?.foto ?? null };
  }, [sp.incomingFrom, sp.ramais]);

  const content = (
    <>
      <style>{`@keyframes sfPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.12)}}`}</style>
      <button
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        data-softphone-ui="true"
        aria-label="Softphone (arraste para mover)"
        title="Clique para abrir · arraste para mover"
        style={{ position: "fixed", right: pos.right, bottom: pos.bottom, zIndex: SOFTPHONE_Z, pointerEvents: "auto", width: 56, height: 56, borderRadius: "50%", background: "#6d1a2e", border: 0, boxShadow: "0 8px 22px rgba(109,26,46,.42)", cursor: "grab", touchAction: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Phone size={24} color="#fff" />
        <span style={{ position: "absolute", top: 3, right: 3, width: 13, height: 13, borderRadius: "50%", background: sp.incoming ? "#34d399" : st.color, border: "2px solid #6d1a2e" }} />
        {sp.inCall && <span style={{ position: "absolute", inset: -3, borderRadius: "50%", border: "2px solid #34d399" }} />}
        {sp.incoming && <span style={{ position: "absolute", inset: -4, borderRadius: "50%", border: "3px solid #34d399", animation: "sfPulse 1s infinite" }} />}
      </button>

      {adEstado && <AutoDialerPainel estado={adEstado} onFechar={() => setAutoDialerFechado(adEstado.sessao.id)} />}

      {sp.open && !adEstado && (
        // ⛔ pointerEvents:"auto" é OBRIGATÓRIO aqui, não é enfeite.
        // Radix Dialog/Sheet aberto põe `pointer-events: none` no <body>. Sem
        // esta linha o painel APARECE na frente (z-index 2147483000) mas nenhum
        // clique chega nele — foi o que aconteceu em 06/08: o Douglas, em
        // ligação, apertava DESLIGAR sobre o modal da Triagem e não acontecia
        // nada. Quem está em ligação precisa desligar de qualquer tela.
        <div
          data-softphone-ui="true"
          style={{ position: "fixed", right: Math.min(pos.right, window.innerWidth - 336), bottom: Math.min(pos.bottom + 68, window.innerHeight - 40), zIndex: SOFTPHONE_Z, pointerEvents: "auto", width: 320, background: "#fbf8f4", borderRadius: 18, boxShadow: "0 14px 44px rgba(109,26,46,.24)", overflow: "hidden", color: "#2b2422", fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" }}
        >
          <div style={{ background: "#6d1a2e", color: "#fff", padding: "11px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Phone size={16} />
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
                <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: "nowrap" }}>Softphone Prime</span>
                {sp.ramal && (
                  <span style={{ fontSize: 10, opacity: 0.85, letterSpacing: 0.2, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: st.color, boxShadow: sp.status === "registrado" ? "0 0 5px #34d399" : "none" }} />
                    ramal {sp.ramal}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
              {!sp.inCall && (
                <button
                  onClick={abrirHistorico}
                  aria-label="Histórico de chamadas"
                  title="Histórico de chamadas"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, border: 0, cursor: "pointer", background: view === "historico" ? "rgba(255,255,255,.22)" : "rgba(255,255,255,.10)", color: "#fff" }}
                >
                  <Clock size={15} />
                </button>
              )}
              {!sp.inCall && (
                <button
                  onClick={abrirRamais}
                  aria-label="Ramais internos"
                  title="Ramais internos"
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 8, border: 0, cursor: "pointer", background: view === "ramais" ? "rgba(255,255,255,.22)" : "rgba(255,255,255,.10)", color: "#fff" }}
                >
                  <Users size={15} />
                </button>
              )}
              <X size={16} style={{ cursor: "pointer", opacity: 0.85 }} onClick={() => sp.setOpen(false)} />
            </div>
          </div>

          {sp.incoming ? (
            <div style={{ padding: "18px 14px 20px", textAlign: "center" }}>
              <div style={{ margin: "0 auto 8px", width: 64, height: 64 }}>
                <Avatar foto={inbound.foto} nome={inbound.nome} size={64} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.2 }}>{inbound.nome || (sp.incomingFrom ? `Ramal ${sp.incomingFrom}` : "Chamada recebida")}</div>
              {inbound.nome && sp.incomingFrom && <div style={{ fontSize: 13, color: "#8a7d75", marginTop: 2 }}>ramal {sp.incomingFrom}</div>}
              <div style={{ fontSize: 13, color: "#6d1a2e", fontWeight: 500, marginTop: 7 }}>chamada recebida…</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 30, marginTop: 16 }}>
                <button onClick={sp.rejectCall} aria-label="Recusar" title="Recusar" style={{ width: 58, height: 58, borderRadius: "50%", border: 0, color: "#fff", cursor: "pointer", background: "#e24b4a", boxShadow: "0 6px 16px rgba(226,75,74,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PhoneOff size={24} />
                </button>
                <button onClick={sp.answerCall} aria-label="Atender" title="Atender" style={{ width: 58, height: 58, borderRadius: "50%", border: 0, color: "#fff", cursor: "pointer", background: "#0f9d63", boxShadow: "0 6px 16px rgba(15,157,99,.38)", display: "flex", alignItems: "center", justifyContent: "center", animation: "sfPulse 1.1s infinite" }}>
                  <Phone size={24} />
                </button>
              </div>
            </div>
          ) : sp.inCall && transferindo ? (
            <div>
              <div style={{ padding: "10px 12px 6px", display: "flex", alignItems: "center", gap: 6 }}>
                <button onClick={() => setTransferindo(false)} aria-label="Voltar" title="Voltar" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 8, border: "1px solid #e7ddd3", background: "#fff", color: "#8a7d75", cursor: "pointer", flexShrink: 0 }}>
                  <ChevronLeft size={16} />
                </button>
                <input value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="transferir para…" style={{ flex: 1, minWidth: 0, boxSizing: "border-box", border: "1px solid #e7ddd3", background: "#fff", borderRadius: 10, padding: "7px 11px", fontSize: 13, color: "#2b2422", outline: "none" }} />
              </div>
              <div style={{ maxHeight: 300, overflowY: "auto", padding: "0 8px 12px" }}>
                {sp.ramais === null && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>carregando ramais…</div>}
                {sp.ramais && grupos.length === 0 && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>nenhum ramal</div>}
                {grupos.map((g) => (
                  <div key={g.equipe}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#a8968b", padding: "9px 8px 3px" }}>{g.equipe}</div>
                    {g.itens.filter((i) => i.ramal !== sp.ramal).map((i) => (
                      <button key={i.ramal} onClick={() => sp.transfer(i.ramal)} title={`Transferir para ${i.nome} (ramal ${i.ramal})`} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 10, border: 0, background: "transparent", cursor: "pointer", textAlign: "left" }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <Avatar foto={i.foto} nome={i.nome} size={28} />
                          <span style={{ position: "absolute", right: -1, bottom: -1, width: 9, height: 9, borderRadius: "50%", background: presencaSet.has(i.ramal) ? "#34d399" : "#c9bdb3", border: "2px solid #fbf8f4" }} />
                        </div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "#2b2422", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.nome}</span>
                        <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: "#6d1a2e", background: "#f5e7eb", borderRadius: 6, padding: "1px 8px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <PhoneForwarded size={11} /> {i.ramal}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : sp.inCall ? (
            <div style={{ padding: "16px 14px 18px", textAlign: "center" }}>
              <div style={{ margin: "0 auto 8px", width: 56, height: 56 }}><Avatar foto={sp.contactFoto} nome={sp.contactName} size={56} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.2 }}>{sp.contactName || sp.number || "Em ligação"}</div>
              {sp.contactName && <div style={{ fontSize: 13, color: "#8a7d75", marginTop: 2 }}>{sp.number}</div>}
              {/* ⛔ Sinal GRANDE e imediato do clique em Desligar — sem isto o
                  clique não mudava nada na tela até o "ended" do JsSIP voltar
                  (round-trip de rede), e parecia que não tinha feito nada.
                  (Douglas 04/09/2026: "apertam o botão de desligar e não
                  desliga... demora") */}
              <div style={{ fontSize: 13, color: sp.encerrando ? "#e24b4a" : "#0f6e56", fontWeight: 500, marginTop: 6 }}>
                {sp.encerrando ? "encerrando…" : `em ligação · ${sp.timer}`}
              </div>
              {emSalaDiscadora ? (
                <div style={{ marginTop: 14, border: "1px solid #e7ddd3", borderRadius: 10, background: "#f5e7eb", padding: "10px 12px", color: "#6d1a2e", fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>
                  Sala conectada. Use os controles da tela da discadora para desligar o cliente, pausar ou transferir.
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginTop: 14 }}>
                  <button onClick={sp.toggleMute} aria-label="Mudo" style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid #e7ddd3", background: sp.muted ? "#f5e7eb" : "#fff", color: sp.muted ? "#6d1a2e" : "#8a7d75", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Mic size={20} />
                  </button>
                  <button
                    onClick={sp.hangup}
                    disabled={sp.encerrando}
                    aria-label={sp.encerrando ? "Encerrando…" : "Desligar"}
                    title={sp.encerrando ? "Encerrando…" : "Desligar"}
                    style={{ width: 64, height: 64, borderRadius: "50%", border: 0, color: "#fff", cursor: sp.encerrando ? "default" : "pointer", background: "#e24b4a", opacity: sp.encerrando ? 0.55 : 1, boxShadow: "0 6px 16px rgba(226,75,74,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <PhoneOff size={26} />
                  </button>
                  <button onClick={() => { setFiltro(""); sp.carregarRamais(); setTransferindo(true); }} aria-label="Transferir" title="Transferir" style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid #e7ddd3", background: "#fff", color: "#6d1a2e", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <PhoneForwarded size={20} />
                  </button>
                </div>
              )}
            </div>
          ) : sp.calling ? (
            <div style={{ padding: "18px 14px 20px", textAlign: "center" }}>
              <div style={{ margin: "0 auto 8px", width: 64, height: 64 }}><Avatar foto={sp.contactFoto} nome={sp.contactName} size={64} /></div>
              <div style={{ fontSize: 15, fontWeight: 500, lineHeight: 1.2 }}>{sp.contactName || sp.number || "Chamando…"}</div>
              {sp.contactName && <div style={{ fontSize: 13, color: "#8a7d75", marginTop: 2 }}>{sp.number}</div>}
              <div style={{ fontSize: 13, color: "#6d1a2e", fontWeight: 500, marginTop: 7 }}>{sp.encerrando ? "cancelando…" : "chamando…"}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 16 }}>
                <button
                  onClick={sp.hangup}
                  disabled={sp.encerrando}
                  aria-label={sp.encerrando ? "Cancelando…" : "Cancelar"}
                  title={sp.encerrando ? "Cancelando…" : "Cancelar"}
                  style={{ width: 58, height: 58, borderRadius: "50%", border: 0, color: "#fff", cursor: sp.encerrando ? "default" : "pointer", background: "#e24b4a", opacity: sp.encerrando ? 0.55 : 1, boxShadow: "0 6px 16px rgba(226,75,74,.35)", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <PhoneOff size={24} />
                </button>
              </div>
            </div>
          ) : view === "ramais" ? (
            <div>
              <div style={{ padding: "10px 12px 6px" }}>
                <input
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="buscar pessoa ou ramal"
                  style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e7ddd3", background: "#fff", borderRadius: 10, padding: "7px 11px", fontSize: 13, color: "#2b2422", outline: "none" }}
                />
              </div>
              <div style={{ maxHeight: 322, overflowY: "auto", padding: "0 8px 12px" }}>
                {sp.ramais === null && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>carregando ramais…</div>}
                {sp.ramais && grupos.length === 0 && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>nenhum ramal encontrado</div>}
                {grupos.map((g) => (
                  <div key={g.equipe}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "#a8968b", padding: "9px 8px 3px" }}>{g.equipe}</div>
                    {g.itens.map((i) => (
                      <button
                        key={i.ramal}
                        onClick={() => sp.call(i.ramal, i.nome, i.foto)}
                        title={`Ligar para ${i.nome} (ramal ${i.ramal})`}
                        style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 10, border: 0, background: "transparent", cursor: "pointer", textAlign: "left" }}
                      >
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <Avatar foto={i.foto} nome={i.nome} size={30} />
                          <span title={presencaSet.has(i.ramal) ? "online" : "offline"} style={{ position: "absolute", right: -1, bottom: -1, width: 9, height: 9, borderRadius: "50%", background: presencaSet.has(i.ramal) ? "#34d399" : "#c9bdb3", border: "2px solid #fbf8f4" }} />
                        </div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "#2b2422", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.nome}</span>
                        <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: "#6d1a2e", background: "#f5e7eb", borderRadius: 6, padding: "1px 8px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <Phone size={11} /> {i.ramal}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : view === "historico" ? (
            <div>
              <div style={{ padding: "10px 12px 6px" }}>
                <input value={buscaHist} onChange={(e) => setBuscaHist(e.target.value)} placeholder="buscar número ou pessoa" style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e7ddd3", background: "#fff", borderRadius: 10, padding: "7px 11px", fontSize: 13, color: "#2b2422", outline: "none" }} />
              </div>
              <div style={{ maxHeight: 288, overflowY: "auto", padding: "0 8px 8px" }}>
                {sp.historico === null && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>carregando…</div>}
                {sp.historico && histFiltrado.length === 0 && <div style={{ padding: "16px 10px", fontSize: 13, color: "#8a7d75", textAlign: "center" }}>{buscaHist.trim() ? "nada encontrado" : "nenhuma gravação ainda"}</div>}
                {histFiltrado.map((h) => (
                  <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 8px", borderRadius: 10 }}>
                    <button onClick={() => tocarGravacao(h.id)} aria-label="Tocar" style={{ flexShrink: 0, width: 34, height: 34, borderRadius: "50%", border: 0, cursor: "pointer", background: playId === h.id ? "#6d1a2e" : "#f5e7eb", color: playId === h.id ? "#fff" : "#6d1a2e", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {loadingPlay === h.id ? <span style={{ fontSize: 10 }}>…</span> : (playId === h.id ? <Pause size={15} /> : <Play size={15} />)}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: "#2b2422", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fmtTel(h.destino) !== "—" ? fmtTel(h.destino) : (h.ramal_origem || "—")}</div>
                      <div style={{ fontSize: 11, color: "#a8968b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.usuario_nome ? `${h.usuario_nome} · ` : ""}{fmtData(h.created_at)} · {fmtDur(h.duracao_segundos)}{h.tipo !== "manual" ? ` · ${TIPO_LABEL[h.tipo] || h.tipo}` : ""}{h.virou_venda ? " · venda" : ""}</div>
                    </div>
                    <button onClick={() => baixarGravacao(h)} aria-label="Baixar" title="Baixar" style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, border: "1px solid #e7ddd3", background: "#fff", color: "#8a7d75", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Download size={14} />
                    </button>
                  </div>
                ))}
              </div>
              {playUrl && (
                <div style={{ padding: "0 8px 10px" }}>
                  <AudioPlayer key={playUrl} src={playUrl} autoPlay />
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ padding: "18px 16px 4px", textAlign: "center" }}>
                <input
                  ref={inputRef}
                  value={sp.number}
                  onChange={(e) => sp.setNumber(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sp.call(sp.number); }}
                  placeholder="digite o número"
                  inputMode="numeric"
                  autoFocus
                  style={{ width: "100%", border: 0, background: "transparent", textAlign: "center", fontSize: 26, fontWeight: 500, color: "#2b2422", outline: "none" }}
                />
                <div style={{ fontSize: 13, color: "#8a7d75", marginTop: 3, minHeight: 18 }}>pronto para discar</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, padding: "12px 24px 8px" }}>
                {KEYS.map(({ k, s }) => (
                  <button key={k} onClick={() => sp.sendDTMF(k)} style={{ height: 48, borderRadius: 14, background: "#fff", border: "1px solid #e7ddd3", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                    <span style={{ fontSize: 19, fontWeight: 600 }}>{k}</span>
                    <span style={{ fontSize: 8, letterSpacing: 1.4, color: "#b6a99d", height: 9 }}>{s || " "}</span>
                  </button>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 30px 20px" }}>
                <button onClick={sp.toggleMute} aria-label="Mudo" style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid #e7ddd3", background: sp.muted ? "#f5e7eb" : "#fff", color: sp.muted ? "#6d1a2e" : "#8a7d75", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Mic size={20} />
                </button>
                <button onClick={() => sp.call(sp.number)} aria-label="Ligar" style={{ width: 64, height: 64, borderRadius: "50%", border: 0, color: "#fff", cursor: "pointer", background: "#6d1a2e", boxShadow: "0 6px 16px rgba(109,26,46,.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Phone size={26} />
                </button>
                <button onClick={() => sp.setNumber(sp.number.slice(0, -1))} aria-label="Apagar" style={{ width: 50, height: 50, borderRadius: "50%", border: "1px solid #e7ddd3", background: "#fff", color: "#8a7d75", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Delete size={20} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  return typeof document !== "undefined" ? createPortal(content, document.body) : content;
}
